import { FileType } from "common/components/Folders";
import { FolderRecord } from "common/records/Folder";
import { RootState } from "common/reducers";
import { List, Map } from "immutable";
import { createSelector } from "reselect";

const rootKey = "folders";
/**
 * Loads byId key from folders
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, FolderRecord>} Map<number, FolderRecord>
 */
export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);

/**
 * Loads lastSavedById key from folders
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, FolderRecord>} Map<number, FolderRecord>
 */
export const lastSavedById = (state: RootState) => state.getIn([rootKey, "lastSavedById"]);

/**
 * Loads an folder by id
 *
 * @param {RootState} state The RootState
 * @param {{ folderId: number }} options
 * @returns {FolderRecord} FolderRecord
 */
export const folder = (state: RootState, props: { folderId: number }) => byId(state).get(props.folderId);

/**
 * Loads an last saved folder by id
 *
 * @param {RootState} state The RootState
 * @param {{ folderId: number }} options
 * @returns {FolderRecord} FolderRecord
 */
export const lastSavedFolder = (state: RootState, props: { folderId: number }) =>
  lastSavedById(state).get(props.folderId);

/**
 * Loads the ancestors of a folder
 *
 * @param {RootState} state The RootState
 * @param {{ folderId: number }} options
 * @returns {FolderRecord} FolderRecord
 */
export const ancestors = createSelector(
  [folder, (state) => state, (_state, props) => props],
  (childFolder, state, props): List<FolderRecord> => {
    let folders = List<FolderRecord>();
    if (!childFolder) {
      return folders;
    }

    if (childFolder.parent_id) {
      folders = folders.concat(ancestors(state, { ...props, folderId: childFolder.parent_id }));
    }
    folders = folders.push(childFolder);
    return folders;
  },
);

/**
 * Loads the ancestor ids of a folder
 *
 * @param {RootState} state The RootState
 * @param {{ folderId: number }} options
 * @returns {number} number
 */
export const ancestorIds = createSelector([ancestors], (ancestors): List<number> => {
  return ancestors.map((folder) => folder.id).slice(1, -1);
});

/**
 * Returns true if there are records with the given folder id or if there are folders with the given folder id
 *
 * @param {RootState} state The RootState
 * @param {{ folderId: number; fileType: FileType}} props
 * @returns {boolean} result
 */
export const folderNotEmpty = (state: RootState, props: { folderId: number; fileType: FileType }): boolean => {
  const records = (state.getIn([props.fileType, "byId"]) as Map<number, any>)?.toList() as List<any>;
  const someRecords = records?.some((record) => record.folder_id === props.folderId);
  const someFolders = state
    .getIn([rootKey, "byId"])
    ?.toList()
    .some((folder) => folder.parent_id === props.folderId);

  return someRecords || someFolders;
};
